import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polygon, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { obtenerDistrito, anillosDeDistrito } from '../../utils/distritos';

const CENTRO_DEFECTO = { lat: -12.117939985496957, lng: -77.02892645376073 };

// Mismo ícono de pin que ya usa `PasoUbicacion.jsx` (`.mp-pin`, definido en
// `MapaPrincipal.css` — CRA lo bundlea globalmente, no hace falta reimportarlo).
const iconoPin = new L.DivIcon({
  className: '',
  html: '<div class="mp-pin" style="transform:rotate(-45deg)"></div>',
  iconSize: [34, 34],
  iconAnchor: [17, 34],
});

const RecentrarMapa = ({ centro }) => {
  const map = useMap();
  useEffect(() => { map.setView(centro, map.getZoom()); }, [centro, map]);
  return null;
};

const CapturarClicks = ({ onClick }) => {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
};

/**
 * Recalcula el tamaño del mapa cuando su contenedor cambia de tamaño o pasa de
 * oculto a visible (las pestañas de configuración están en `display:none` al
 * inicializar, y Leaflet medía mal: solo pintaba una esquina).
 */
const AjustarTamano = () => {
  const map = useMap();
  useEffect(() => {
    const cont = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(cont);
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, [map]);
  return null;
};

// Centra el mapa en el distrito elegido y avisa su polígono (para dibujarlo).
const CentrarDistrito = ({ nombre, onGeo }) => {
  const map = useMap();
  useEffect(() => {
    if (!nombre) return undefined;
    let cancelado = false;
    (async () => {
      const info = await obtenerDistrito(nombre);
      if (cancelado || !info) return;
      if (info.lat && info.lon) {
        map.setView([Number(info.lat), Number(info.lon)], 14, { animate: true });
      }
      onGeo(info.geojson || null);
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nombre, map]);
  return null;
};

/**
 * Selector de coordenadas por mapa (HU-009-B) — clic o arrastre del pin para
 * fijar `lat`/`lng`. Si recibe `distrito`, centra el mapa en él y dibuja su
 * contorno (mismo comportamiento que la página de inicio).
 */
const MapaSelectorCoordenadas = ({ lat, lng, onCambiar, distrito }) => {
  // `useMemo`: si `posicion` fuera un objeto nuevo en cada render, `RecentrarMapa`
  // volvería a centrar al punto guardado y pisaría el centrado por distrito.
  const posicion = useMemo(
    () => ((lat != null && lng != null) ? { lat: Number(lat), lng: Number(lng) } : CENTRO_DEFECTO),
    [lat, lng],
  );
  const [geoDistrito, setGeoDistrito] = useState(null);

  return (
    <div className="panel-via-mapa">
      <MapContainer center={posicion} zoom={16} style={{ height: '260px', width: '100%', borderRadius: '12px' }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap'
        />
        <AjustarTamano />
        <CentrarDistrito nombre={distrito} onGeo={setGeoDistrito} />
        {geoDistrito && anillosDeDistrito(geoDistrito).map((anillo, i) => (
          <Polygon
            key={`distrito-zona-${i}`}
            positions={anillo}
            pathOptions={{ color: '#1a73e8', weight: 2, fillColor: '#1a73e8', fillOpacity: 0.05 }}
          />
        ))}
        <RecentrarMapa centro={posicion} />
        <CapturarClicks onClick={(latlng) => onCambiar(latlng.lat, latlng.lng)} />
        <Marker
          position={posicion}
          icon={iconoPin}
          draggable
          eventHandlers={{
            dragend: (e) => {
              const { lat: nuevaLat, lng: nuevaLng } = e.target.getLatLng();
              onCambiar(nuevaLat, nuevaLng);
            },
          }}
        />
      </MapContainer>
      <p className="panel-texto-ayuda">Haz clic en el mapa o arrastra el pin para fijar la coordenada exacta.</p>
    </div>
  );
};

export default MapaSelectorCoordenadas;
